/**
 * ルールの一覧。
 *
 * 一覧が守っていること:
 * - **数えていない件数を 0 と書かない。** デモは「生成 / 配信」の列を持つが、
 *   ルール単位で数える仕組みが API に無いので、代わりに数えていないと出す
 * - 配信中（RUN）かどうかは公開に直結するので、一覧でも必ず見えるようにする
 * - ここでは run / stop を押せない。切り替えは詳細で確認を挟んでから行う
 *   （一覧に並んだスイッチは、隣の行を触ってしまう）
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  CreateRuleDocument,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  RulesDocument,
  formatJstDateTime,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import {
  RULE_COUNTS_NOT_MEASURED,
  RULE_COUNTS_NOT_MEASURED_NOTE,
  SUPPORTS_RULE_DELETE,
} from "../api-capabilities";
import { ruleVersionDisplayName } from "../rule-version-name";
import { RuleStateTag, type RuleUserError } from "./RuleParts";

export function RulesPage() {
  const navigate = useNavigate();
  const [result, refetch] = useQuery({ query: RulesDocument });
  const [createState, createRule] = useMutation(CreateRuleDocument);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [userErrors, setUserErrors] = useState<RuleUserError[]>([]);

  const rules = result.data?.rules ?? [];

  const submit = (): void => {
    void createRule({
      input: { name: name.trim(), description: description.trim() },
    }).then((mutationResult) => {
      if (mutationResult.error !== undefined) {
        setUserErrors([
          {
            field: null,
            code: "INTERNAL_ERROR",
            message: `作れませんでした: ${mutationResult.error.message}`,
          },
        ]);
        return;
      }
      const payload = mutationResult.data?.createRule;
      if (payload === undefined) {
        return;
      }
      if (payload.userErrors.length > 0) {
        setUserErrors([...payload.userErrors]);
        return;
      }
      setUserErrors([]);
      setCreating(false);
      setName("");
      setDescription("");
      const created = payload.rule;
      if (created !== null && created !== undefined) {
        navigate(`/rules/${created.id}`);
      }
    });
  };

  return (
    <div className="vd-page">
      <PageHeader
        title="ルール"
        description="どの商品に・どのプロンプトで答えを作るか。配信されるのは RUN のルールが作った答えだけです。"
        titleExtra={<HelpIcon slug="rules/list" />}
        extra={
          <span className="vd-row">
            {creating ? null : (
              <Button variant="primary" onClick={() => setCreating(true)}>
                ルールを作る
              </Button>
            )}
          </span>
        }
      />

      <div className="vd-stack">
        {creating ? (
          <Card
            title="ルールを作る"
            note="作った直後は止まった状態（STOP）で、最初の版は既定の指示が入ります。配信は版を決めてから切り替えます。"
          >
            {userErrors.map((error) => (
              <span
                key={`${error.code}-${error.message}`}
                className="vd-field-error"
                role="alert"
              >
                {error.message}
              </span>
            ))}
            <div className="vd-field">
              <label htmlFor="rule-name">名前</label>
              <input
                id="rule-name"
                className="vd-input"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="vd-field">
              <label htmlFor="rule-description">説明</label>
              <input
                id="rule-description"
                className="vd-input"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <div className="vd-row" style={{ marginTop: 12 }}>
              <Button
                variant="primary"
                disabled={createState.fetching || name.trim() === ""}
                onClick={submit}
              >
                {createState.fetching ? "作っています" : "作る"}
              </Button>
              <Button
                disabled={createState.fetching}
                onClick={() => {
                  setCreating(false);
                  setUserErrors([]);
                }}
              >
                やめる
              </Button>
            </div>
          </Card>
        ) : null}

        {result.error !== undefined ? (
          <ErrorState
            description={result.error.message}
            onRetry={() => refetch({ requestPolicy: "network-only" })}
          />
        ) : result.fetching && result.data === undefined ? (
          <LoadingState />
        ) : rules.length === 0 ? (
          <EmptyState
            title="まだルールがありません"
            description="1 本作ると、そのプロンプトで答えを作って試せるようになります。"
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                ルールを作る
              </Button>
            }
          />
        ) : (
          rules.map((rule) => (
            <Card
              key={rule.id}
              title={
                <>
                  {rule.name}
                  <RuleStateTag state={rule.state} />
                </>
              }
              extra={<Link to={`/rules/${rule.id}`}>開く</Link>}
              note={
                rule.lastRunAt === null
                  ? "まだ一度も生成していません"
                  : `最後に生成を始めた時刻 ${formatJstDateTime(rule.lastRunAt)}`
              }
            >
              <p style={{ marginTop: 0 }}>{rule.description}</p>
              <div className="vd-grid">
                <div>
                  <div className="vd-stat-label">対象</div>
                  <div className="vd-stat-value">{rule.scope ?? "全商品"}</div>
                </div>
                <div>
                  <div className="vd-stat-label">配信中の版</div>
                  <div className="vd-stat-value">
                    {rule.publishedPromptVersion === null
                      ? "まだありません"
                      : ruleVersionDisplayName(
                          rule.id,
                          rule.publishedPromptVersion,
                          // 一覧は版の保存の時刻を引いていない（ルール 1 本ごとに
                          // 版の一覧を読むことになる）。**推測で日時を埋めない**
                          null,
                          formatJstDateTime,
                        )}
                  </div>
                </div>
                <div>
                  <div className="vd-stat-label">1 商品の枠</div>
                  <div className="vd-stat-value">{rule.maxPerProduct} 件</div>
                </div>
                <div>
                  <div className="vd-stat-label">生成した答え</div>
                  <div className="vd-stat-value">
                    {RULE_COUNTS_NOT_MEASURED}
                  </div>
                  <div className="vd-stat-note">
                    {RULE_COUNTS_NOT_MEASURED_NOTE}
                  </div>
                </div>
              </div>
              {rule.banned.length === 0 ? null : (
                <p className="vd-stat-note" style={{ marginBottom: 0 }}>
                  禁止表現: {rule.banned.join(" / ")}
                </p>
              )}
            </Card>
          ))
        )}

        {SUPPORTS_RULE_DELETE ? null : (
          <p className="vd-stat-note" style={{ margin: 0 }}>
            ルールを消す口はまだありません。使わなくなったものは STOP
            に戻してください。
          </p>
        )}
      </div>
    </div>
  );
}
